import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import { type ReactNode, useId, useRef, useState } from 'react';

import * as styles from './permission-section.css';

export interface PermissionExpandableSectionProps {
  title: string;
  subtitle?: string;
  defaultCollapsed?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  dataTestId?: string;
  children: ReactNode;
}

/** A stable region: collapsing preserves drafts and removes its controls from focus. */
export function PermissionExpandableSection({
  title,
  subtitle,
  defaultCollapsed = false,
  onExpandedChange,
  dataTestId,
  children,
}: PermissionExpandableSectionProps) {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  return (
    <section
      className={styles.section}
      data-sl-state={expanded ? 'expanded' : 'collapsed'}
      data-testid={dataTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        id={`${id}-trigger`}
        aria-expanded={expanded}
        aria-controls={`${id}-region`}
        className={styles.trigger}
        onClick={() => {
          const next = !expanded;
          setExpanded(next);
          onExpandedChange?.(next);
        }}
      >
        <span className={styles.heading}>
          <span className={styles.title}>{title}</span>
          {subtitle ? (
            <span className={styles.subtitle}>{subtitle}</span>
          ) : null}
        </span>
        <DisclosureChevron expanded={expanded} className={styles.chevron} />
      </button>
      <AnimatedRegion
        expanded={expanded}
        animateContent
        returnFocusRef={triggerRef}
        id={`${id}-region`}
        role="region"
        aria-labelledby={`${id}-trigger`}
        className={styles.region}
      >
        <div className={styles.content}>{children}</div>
      </AnimatedRegion>
    </section>
  );
}
